import React, { useState, useCallback, useMemo, useRef } from 'react';
import { createEditor, Transforms, Editor, Node, Range } from 'slate';
import { Slate, Editable, withReact, useSlate, ReactEditor } from 'slate-react';
import { withHistory } from 'slate-history';
import {
  Box,
  HStack,
  VStack,
  IconButton,
  Divider,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Input,
  Button,
  FormControl,
  FormLabel,
  useDisclosure,
  Text,
  Icon,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
} from '@chakra-ui/react';
import { IoSend } from 'react-icons/io5';
import {
  FiBold,
  FiItalic,
  FiLink,
  FiPaperclip,
  FiSmile,
  FiUpload,
} from 'react-icons/fi';
import { MdFormatStrikethrough, MdClose } from 'react-icons/md';
import EmojiPicker from 'emoji-picker-react';

// Helper functions
const insertLink = (editor, url, text) => {
  if (!url) return;
  
  const { selection } = editor;
  const isCollapsed = selection && Range.isCollapsed(selection);
  
  if (isCollapsed) {
    const link = {
      type: 'link',
      url,
      children: [{ text: text || url }],
    };
    Transforms.insertNodes(editor, link);
  } else {
    Transforms.wrapNodes(
      editor,
      { type: 'link', url, children: [] },
      { split: true }
    );
    Transforms.collapse(editor, { edge: 'end' });
  }
};

// Format button component
const FormatButton = ({ format, icon, label, isFocused }) => {
  const editor = useSlate();
  
  const isActive = () => {
    try {
      // Check if we have a valid selection first
      const { selection } = editor;
      if (!selection) return false;
      
      // Try to get marks only if we're in a text node
      const [match] = Editor.nodes(editor, {
        match: n => n.text !== undefined,
        mode: 'lowest',
      });
      
      if (!match) return false;
      
      const marks = Editor.marks(editor);
      return marks ? marks[format] === true : false;
    } catch (err) {
      // If we can't get marks (e.g., in a list), return false
      return false;
    }
  };
  
  const toggleFormat = (e) => {
    e.preventDefault();
    
    try {
      const isCurrentlyActive = isActive();
      
      if (isCurrentlyActive) {
        Editor.removeMark(editor, format);
      } else {
        Editor.addMark(editor, format, true);
      }
    } catch (err) {
      // Ignore errors when trying to toggle marks in non-text nodes
      // Cannot toggle mark in current selection
    }
  };

  // Calculate active state once
  const active = isActive();

  return (
    <Tooltip label={label} fontSize="xs" placement="top">
      <IconButton
        icon={icon}
        size="xs"
        variant="ghost"
        color={active ? "gray.800" : (isFocused ? "gray.600" : "gray.400")}
        bg={active ? "gray.100" : "transparent"}
        onMouseDown={toggleFormat}
        _hover={{
          bg: active ? "gray.100" : "gray.50",
          color: "gray.800",
        }}
        _active={{
          bg: "gray.100",
          color: "gray.800",
        }}
        aria-label={label}
        h="26px"
        w="26px"
        minW="26px"
      />
    </Tooltip>
  );
};

// Link button with modal
const LinkButton = ({ setModalOpen, isFocused }) => {
  const editor = useSlate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');

  const handleOpen = () => {
    const { selection } = editor;
    if (selection && !Range.isCollapsed(selection)) {
      const text = Editor.string(editor, selection);
      setLinkText(text);
    }
    setModalOpen(true);
    onOpen();
  };

  const handleClose = () => {
    setModalOpen(false);
    onClose();
    setLinkUrl('');
    setLinkText('');
  };

  const handleLinkSubmit = () => {
    if (linkUrl) {
      insertLink(editor, linkUrl, linkText);
      setLinkUrl('');
      setLinkText('');
      setModalOpen(false);
      onClose();
      ReactEditor.focus(editor);
    }
  };

  return (
    <>
      <Tooltip label="Add Link (Ctrl+K)" fontSize="xs" placement="top">
        <IconButton
          icon={<FiLink />}
          size="xs"
          variant="ghost"
          color={isFocused ? "gray.600" : "gray.400"}
          onMouseDown={(e) => {
            e.preventDefault();
            handleOpen();
          }}
          _hover={{
            bg: "gray.50",
            color: "gray.800",
          }}
          _active={{
            bg: "gray.100",
            color: "gray.800",
          }}
          aria-label="Add Link"
          h="26px"
          w="26px"
          minW="26px"
        />
      </Tooltip>

      <Modal isOpen={isOpen} onClose={handleClose} size="md" closeOnOverlayClick={false} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="lg" fontWeight="600" color="gray.900">
            Add Link
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={4}>
              <FormLabel fontSize="sm" color="gray.700">
                Link Text
              </FormLabel>
              <Input
                value={linkText}
                onChange={(e) => setLinkText(e.target.value)}
                placeholder="Enter link text"
                size="sm"
                borderColor="gray.200"
                _hover={{ borderColor: "gray.300" }}
                _focus={{
                  borderColor: "gray.400",
                  boxShadow: "0 0 0 1px rgba(0,0,0,0.05)",
                }}
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm" color="gray.700">
                URL
              </FormLabel>
              <Input
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://example.com"
                size="sm"
                borderColor="gray.200"
                _hover={{ borderColor: "gray.300" }}
                _focus={{
                  borderColor: "gray.400",
                  boxShadow: "0 0 0 1px rgba(0,0,0,0.05)",
                }}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              size="sm"
              mr={2}
              color="gray.600"
              onClick={handleClose}
              _hover={{ bg: "gray.50" }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              bg="gray.900"
              color="white"
              onClick={handleLinkSubmit}
              isDisabled={!linkUrl}
              _hover={{ bg: "gray.800" }}
              _active={{ bg: "gray.700" }}
            >
              Add Link
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

// Leaf renderer for applying styles
const Leaf = ({ attributes, children, leaf }) => {
  if (leaf.bold) {
    children = <strong>{children}</strong>;
  }
  if (leaf.italic) {
    children = <em>{children}</em>;
  }
  if (leaf.underline) {
    children = <u>{children}</u>;
  }
  if (leaf.strikethrough) {
    children = <s>{children}</s>;
  }
  
  return <span {...attributes}>{children}</span>;
};

// Element renderer
const Element = ({ attributes, children, element }) => {
  const { type } = element;
  
  switch (type) {
    case 'paragraph':
      return <p {...attributes}>{children}</p>;
    case 'link':
      return (
        <a
          {...attributes}
          href={element.url}
          style={{ color: '#4A5568', textDecoration: 'underline' }}
          onClick={e => e.preventDefault()}
        >
          {children}
        </a>
      );
    default:
      return <div {...attributes}>{children}</div>;
  }
};

// Main editor component
export default function SimpleRichTextEditor({ 
  onSendMessage, 
  onFileAttach, 
  uploadingFile = false,
  isSending = false,
  onTyping,
  placeholder = "Message",
  selectedFiles = [],
  onRemoveFile = () => {},
  getFileIcon = () => null,
  formatFileSize = () => "",
  onFilesDropped = null // New prop for handling dropped files
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiButtonRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);
  
  // Initial value - must be a constant
  const initialValue = [
    {
      type: 'paragraph',
      children: [{ text: '' }],
    },
  ];
  
  // Create editor instance
  const editor = useMemo(() => withHistory(withReact(createEditor())), []);
  
  const [value, setValue] = useState(initialValue);

  // Render element
  const renderElement = useCallback(props => <Element {...props} />, []);
  
  // Render leaf
  const renderLeaf = useCallback(props => <Leaf {...props} />, []);

  // Check if editor is empty
  const isEditorEmpty = () => {
    const content = Node.string(editor);
    return !content || content.trim() === '';
  };

  // Serialize content to markdown format
  const serialize = () => {
    const serializeNode = (node) => {
      if (node.text !== undefined) {
        // It's a text node
        return serializeLeaf(node);
      }
      
      // It's an element node
      const children = node.children.map(serializeNode).join('');
      
      switch (node.type) {
        case 'paragraph':
          return children + '\n';
        case 'link':
          return `[${children}](${node.url})`;
        default:
          return children;
      }
    };
    
    return value.map(serializeNode).join('').trim();
  };

  const serializeLeaf = (leaf) => {
    let text = leaf.text || '';
    if (leaf.bold) text = `*${text}*`;
    if (leaf.italic) text = `_${text}_`;
    if (leaf.underline) text = `__${text}__`;
    if (leaf.strikethrough) text = `~${text}~`;
    return text;
  };

  // Handle emoji click
  const handleEmojiClick = (emojiData) => {
    // Insert emoji at current cursor position
    Transforms.insertText(editor, emojiData.emoji);
    ReactEditor.focus(editor);
    
    // Close emoji picker
    setShowEmojiPicker(false);
  };

  // Drag and drop handlers
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      if (onFilesDropped) {
        // Use the dedicated handler for dropped files if provided
        onFilesDropped(e.dataTransfer.files);
      } else {
        // Fallback: Create a synthetic event object that mimics file input change event
        const syntheticEvent = {
          target: {
            files: e.dataTransfer.files
          }
        };
        
        // Find the hidden file input and set its files
        const fileInput = document.querySelector('input[type="file"][hidden]');
        if (fileInput && fileInput.onchange) {
          // Trigger the onChange handler directly with our synthetic event
          fileInput.onchange(syntheticEvent);
        }
      }
    }
  }, [onFilesDropped]);

  // Handle send
  const handleSend = () => {
    // Check if we have files or text to send
    if (isEditorEmpty() && (!selectedFiles || selectedFiles.length === 0)) return;
    
    const messageText = isEditorEmpty() ? null : serialize();
    
    // Send message (will handle both files and text)
    onSendMessage(messageText);
    
    // Only clear editor if we sent text
    if (messageText) {
      // Clear the editor safely
      try {
        // Select all content
        Transforms.select(editor, {
          anchor: Editor.start(editor, []),
          focus: Editor.end(editor, [])
        });
        
        // Insert empty text to replace selection
        Transforms.insertText(editor, '');
        
      } catch (e) {
        // If there's an error, reset the entire editor
        editor.children = [{
          type: 'paragraph',
          children: [{ text: '' }]
        }];
        editor.selection = {
          anchor: { path: [0, 0], offset: 0 },
          focus: { path: [0, 0], offset: 0 }
        };
      }
      
      // Reset value state to force re-render
      setValue([
        {
          type: 'paragraph',
          children: [{ text: '' }],
        },
      ]);
    }
    
    // Focus back on editor
    setTimeout(() => {
      ReactEditor.focus(editor);
    }, 0);
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (event) => {
    // Send on Enter (but allow Shift+Enter for new lines)
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
      return;
    }

    // Format shortcuts
    if (!event.ctrlKey && !event.metaKey) return;

    switch (event.key.toLowerCase()) {
      case 'b':
        event.preventDefault();
        try {
          Editor.addMark(editor, 'bold', true);
        } catch (err) {
          // Ignore if we can't add mark (e.g., in non-text node)
        }
        break;
      case 'i':
        event.preventDefault();
        try {
          Editor.addMark(editor, 'italic', true);
        } catch (err) {
          // Ignore
        }
        break;
      case 'u':
        event.preventDefault();
        try {
          Editor.addMark(editor, 'underline', true);
        } catch (err) {
          // Ignore
        }
        break;
      case 'x':
        if (event.shiftKey) {
          event.preventDefault();
          try {
            Editor.addMark(editor, 'strikethrough', true);
          } catch (err) {
            // Ignore
          }
        }
        break;
      default:
        break;
    }
  };

  return (
    <Box 
      bg="white"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      position="relative"
    >
      {/* Drag overlay */}
      {isDragging && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="gray.900"
          opacity={0.9}
          zIndex={10}
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderRadius="8px"
          pointerEvents="none"
        >
          <VStack spacing={2}>
            <Icon as={FiUpload} fontSize="32px" color="white" />
            <Text color="white" fontSize="16px" fontWeight="500">
              Drop files here to attach
            </Text>
          </VStack>
        </Box>
      )}
      <Slate 
        editor={editor} 
        initialValue={initialValue}
        value={value}
        onChange={newValue => {
          setValue(newValue);
          if (onTyping) onTyping();
        }}
      >
        <Box
          px={5}
          py={3}
          borderTop="1px solid"
          borderColor="gray.200"
        >
          <Box
            border="1px solid"
            borderColor={isDragging ? "gray.500" : "gray.300"}
            borderRadius="8px"
            bg="white"
            transition="border-color 0.2s"
            _focusWithin={{
              borderColor: "gray.400",
              boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.02)"
            }}
          >
            {/* Toolbar Section */}
            <HStack
              spacing={0.5}
              px={3}
              py={1.5}
              borderBottom="1px solid"
              borderColor="gray.200"
              bg="gray.50"
              borderTopRadius="8px"
            >
              {/* Text Format Tools */}
              <HStack spacing={0.5}>
                <FormatButton 
                  format="bold" 
                  icon={<FiBold />} 
                  label="Bold (Ctrl+B)"
                  isFocused={isFocused}
                />
                <FormatButton 
                  format="italic" 
                  icon={<FiItalic />} 
                  label="Italic (Ctrl+I)"
                  isFocused={isFocused}
                />
                <FormatButton 
                  format="strikethrough" 
                  icon={<MdFormatStrikethrough />} 
                  label="Strikethrough (Ctrl+Shift+X)"
                  isFocused={isFocused}
                />
              </HStack>
              
              <Divider orientation="vertical" h="16px" borderColor={isFocused ? "gray.300" : "gray.200"} />
              
              <LinkButton setModalOpen={setModalOpen} isFocused={isFocused} />
              
            </HStack>
            
            {/* Input Area */}
            <Box px={3} py={2}>
              {/* Text input */}
              <Box 
                fontSize="14px"
                minH="60px"
                maxH="200px"
                overflowY="auto"
                sx={{
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    bg: 'gray.50',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    bg: 'gray.300',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    bg: 'gray.400',
                  },
                }}
              >
                <Editable
                  renderElement={renderElement}
                  renderLeaf={renderLeaf}
                  placeholder={placeholder}
                  onKeyDown={handleKeyDown}
                  onFocus={() => {
                    setIsFocused(true);
                  }}
                  onBlur={() => {
                    if (!modalOpen && !showEmojiPicker) {
                      setTimeout(() => {
                        setIsFocused(false);
                      }, 200);
                    }
                  }}
                  style={{
                    minHeight: '52px',
                    maxHeight: '200px',
                    outline: 'none',
                    lineHeight: '1.46',
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-wrap',
                    overflowWrap: 'break-word',
                  }}
                />
              </Box>
              
              {/* File Preview - inline chips */}
              {selectedFiles && selectedFiles.length > 0 && (
                <Box 
                  mt={2} 
                  display="flex"
                  flexWrap="wrap"
                  gap={1}
                  w="full"
                >
                  {selectedFiles.map((file, index) => (
                    <HStack
                      key={index}
                      px={1.5}
                      py={1}
                      bg="gray.50"
                      borderRadius="4px"
                      fontSize="10px"
                      spacing={1}
                      flexShrink={0}
                      w="fit-content"
                      maxW="120px"
                    >
                      <Icon as={getFileIcon(file.type)} color="gray.500" fontSize="12px" flexShrink={0} />
                      <Text color="gray.600" noOfLines={1} flex={1} minW={0}>
                        {file.name}
                      </Text>
                      <IconButton
                        icon={<MdClose />}
                        size="xs"
                        variant="ghost"
                        color="gray.400"
                        minW="14px"
                        h="14px"
                        w="14px"
                        p={0}
                        _hover={{ color: "gray.600", bg: "transparent" }}
                        onClick={() => onRemoveFile(index)}
                        aria-label="Remove file"
                      />
                    </HStack>
                  ))}
                </Box>
              )}
              
              {/* Bottom buttons section */}
              <HStack justify="space-between" mt={2}>
                {/* Left side: Attachment and Emoji */}
                <HStack spacing={0.5}>
                  {/* File Attachment */}
                  <Tooltip label="Attach file" fontSize="xs" placement="top">
                    <IconButton
                      icon={<FiPaperclip />}
                      size="xs"
                      variant="ghost"
                      color={isFocused ? "gray.600" : "gray.400"}
                      minW="auto"
                      h="26px"
                      w="26px"
                      _hover={{ 
                        bg: "gray.50",
                        color: "gray.800"
                      }}
                      _active={{
                        bg: "gray.100",
                        color: "gray.800"
                      }}
                      onClick={onFileAttach}
                      isDisabled={uploadingFile}
                      aria-label="Attach file"
                    />
                  </Tooltip>
                  
                  {/* Emoji picker */}
                  <Popover
                    isOpen={showEmojiPicker}
                    onClose={() => setShowEmojiPicker(false)}
                    placement="top-start"
                    closeOnBlur={true}
                    closeOnEsc={true}
                  >
                    <PopoverTrigger>
                      <Box>
                        <Tooltip label="Add emoji" fontSize="xs" placement="top">
                          <IconButton
                            ref={emojiButtonRef}
                            icon={<FiSmile />}
                            size="xs"
                            variant="ghost"
                            color={isFocused ? "gray.600" : "gray.400"}
                            minW="auto"
                            h="26px"
                            w="26px"
                            _hover={{ 
                              bg: "gray.50",
                              color: "gray.800"
                            }}
                            _active={{
                              bg: "gray.100",
                              color: "gray.800"
                            }}
                            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                            aria-label="Add emoji"
                          />
                        </Tooltip>
                      </Box>
                    </PopoverTrigger>
                    <PopoverContent 
                      w="auto" 
                      border="1px solid" 
                      borderColor="gray.200"
                      boxShadow="lg"
                    >
                      <PopoverArrow />
                      <PopoverBody p={0}>
                        <EmojiPicker
                          onEmojiClick={handleEmojiClick}
                          height={350}
                          width={300}
                          previewConfig={{ showPreview: false }}
                          skinTonesDisabled
                          searchPlaceHolder="Search emoji..."
                          lazyLoadEmojis={true}
                        />
                      </PopoverBody>
                    </PopoverContent>
                  </Popover>
                </HStack>
                
                {/* Right side: Send button */}
                <Tooltip label={isSending ? "Sending..." : "Send message"} fontSize="xs" placement="top">
                  <IconButton
                    icon={<IoSend />}
                    size="xs"
                    variant="ghost"
                    color={(!isEditorEmpty() || (selectedFiles && selectedFiles.length > 0)) ? "gray.700" : "gray.300"}
                    minW="auto"
                    h="26px"
                    w="26px"
                    opacity={(!isEditorEmpty() || (selectedFiles && selectedFiles.length > 0)) ? 1 : 0.5}
                    _hover={{ 
                      bg: (!isEditorEmpty() || (selectedFiles && selectedFiles.length > 0)) ? "gray.50" : "transparent",
                      color: (!isEditorEmpty() || (selectedFiles && selectedFiles.length > 0)) ? "gray.900" : "gray.300",
                      opacity: 1
                    }}
                    _active={{
                      bg: (!isEditorEmpty() || (selectedFiles && selectedFiles.length > 0)) ? "gray.100" : "transparent",
                      color: (!isEditorEmpty() || (selectedFiles && selectedFiles.length > 0)) ? "gray.900" : "gray.300"
                    }}
                    onClick={handleSend}
                    isLoading={uploadingFile || isSending}
                    isDisabled={(isEditorEmpty() && (!selectedFiles || selectedFiles.length === 0)) || isSending}
                    aria-label="Send message"
                  />
                </Tooltip>
              </HStack>
            </Box>
          </Box>
        </Box>
      </Slate>
    </Box>
  );
}